import { ChangeDetectorRef, Component, effect, EventEmitter, input, Input, InputSignal, OnDestroy, Output } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { RegimenService } from '@services/regimen-service';
import { AppConfig } from '../../../../app.config';

@Component({
  selector: 'user-view-regimen-preparation',
  templateUrl: './user-view-regimen-preparation.html',
  standalone: false,
})
export class UserViewRegimenPreparationComponent implements OnDestroy {
  regimen: InputSignal<any> = input();
  experiments: InputSignal<any[]> = input();
  @Input('renderReport')
  set reportRendition(showReport: boolean) {
    if (showReport) this.showSkinReport = true;
    else this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'regimen-preparation' }));
  }
  @Output('showRegimen') showRegimen: EventEmitter<any> = new EventEmitter<any>();
  @Output('hideToolBars') hideToolBars: EventEmitter<any> = new EventEmitter<any>();
  user: any;
  showPreparationTimer: boolean = false;
  timer: any = { minutes: 0, seconds: 0 };
  interval: any = {};
  primaryConcern: string = '';
  otherConcerns: string = '';
  allocatedDoctor: any;
  concerns: Array<any> = [];
  defaultTime: number = 120_000;
  elapsedTime: number = 0;
  isPaidUser: boolean = false;
  showSkinReport: boolean = false;
  showRegimenToast: boolean = false;
  remTime: number;
  startTimer: number;
  acqRebranding2025: boolean = false;
  isNewDoctorUiExperimentEnabled: boolean = false;
  regimenPreparationTimerVariant: {
    activationDelayInSeconds: number,
  } = {
    activationDelayInSeconds: 120
  };
  TIMER_MESSAGE_KEYS = {
    GETTING_TREATMENT_PLAN_READY: 'gettingTreatmentPlanReady',
    MATCHING_BEST_INGREDIENTS: 'matchingBestIngredients',
    FINDING_RIGHT_PRODUCTS: 'findingRightProducts',
    ADDING_FACE_WASH: 'addingFaceWash',
    ADDING_SUNSCREEN_MOISTURISER: 'addingSunscreenMoisturiser',
    ADDING_REPAIR_FOR_CONCERN: 'addingRepairForConcern',
    FINDING_BEST_SUPPLEMENT: 'findingBestSupplement',
    ALMOST_DONE: 'almostDone'
  } as const;
  readonly TIMER_MESSAGE_VALUES = Object.values(this.TIMER_MESSAGE_KEYS);
  private readonly MILLISECONDS_PER_SECOND = 1000;
  private readonly SECONDS_PER_MINUTE = 60;

  constructor(public conn: ConnectionService,
    public appConfig: AppConfig,
    private broadcast: BroadcastService,
    private eventLogger: EventLoggerService,
    private changeDetectionRef: ChangeDetectorRef,
    private appWebBridge: AppWebBridgeService,
    private readonly regimenService: RegimenService) {
      effect(async () => {
        if (this.experiments()) await this.setUserExperiments();
      })
  }

  async ngOnInit(): Promise<any> {
    this.startTimer = new Date().getTime();
    this.user = await this.conn.getActingUser();
    if (!this.user?.get('allocatedDoctor')) this.user = await this.conn?.findUserByObjectId(this.user?.id);
    await this.showRegimenTimer();
    await this.fetchAllocatedDoctorForUser();
    this.extractConcernsAndLogPageOpen();
    this.hideToolBars.emit();
    this.updatePaidUserFlag();
    this.changeDetectionRef.detectChanges();
    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('Timer_screen_loading_time', { timeInMilliSec: time, userId: this.user.id });
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'pageOpenRegimenTimer' },
      firebase: { name: 'pageOpenRegimenTimer' },
    });
  }

  async setUserExperiments(): Promise<void> {
    this.experiments().forEach((each: any) => {
      if (each.key === 'regimen_rehaul_v2') {
        this.regimenService.setRegimenVariantImages(this.regimen, false);
      }
      if (each.key === "acq_rebranding_2025") {
        this.acqRebranding2025 = true;
      }
      if (each.key === 'tree_personalized_regimen_activation') {
        this.regimenPreparationTimerVariant = each.variant;
      }
      if (each.key === 'skin_report_new_Dr_UI') {
        this.isNewDoctorUiExperimentEnabled = true;
      }
    });
  }

  async fetchAllocatedDoctorForUser(): Promise<void> {
    if (this.user.get('allocatedDoctor')?.id) {
      this.allocatedDoctor = await this.conn.findUserByObjectId(this.user.get('allocatedDoctor').id);
    }
  }

  extractConcernsAndLogPageOpen(): void {
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'regimen-preparation-new-ui', regimenClass: this.regimen().class }));
    this.concerns = this.user?.get('aiDetections');
    if (this.concerns?.length) {
      this.primaryConcern = this.concerns[0];
      this.otherConcerns = this.concerns.slice(1).join(', ');
    }
  }

  updatePaidUserFlag(): void {
    this.isPaidUser = [
      this.appConfig.Shared.User.OrderState.PROCESSING,
      this.appConfig.Shared.User.OrderState.DELIVERED,
    ].includes(this.user.get('orderState'));
  }

  async showRegimenTimer(): Promise<any> {
    this.defaultTime = (this.regimenPreparationTimerVariant?.activationDelayInSeconds || 120) * 1000;
    const currTime: any = new Date();
    const regimenShowTime: any = new Date(this.regimen().activeFrom.iso);
    this.remTime = regimenShowTime - currTime;
    this.elapsedTime = -(this.defaultTime - (regimenShowTime - currTime));
    if (this.elapsedTime >= this.defaultTime) this.elapsedTime = -this.defaultTime;
    setTimeout((): void => this.startRegimenTimer((regimenShowTime - currTime)));
  }
  // Calculate remaining minutes and seconds from milliseconds
  calculateRemainingTime(milliseconds: number): { minutes: number; seconds: number } {
    const minutes = Math.floor(milliseconds / 1000 / 60);
    const seconds = Math.floor((milliseconds - (minutes * 1000 * 60)) / 1000);
    return { minutes, seconds };
  }

  // Start animation after a delay
  startAnimationAfterDelay(delay: number): void {
    setTimeout((): boolean => this.timer.animate = true, delay);
  }

  // Update the timer and check if it's finished
  updateTimerAndCheckIfFinished(): void {
    if (this.timer.seconds === 0) {
      this.timer.seconds = 59;
      this.timer.minutes -= 1;
    } else {
      this.timer.seconds -= 1;
    }

    // Trigger change detection to update dynamic message
    this.changeDetectionRef.detectChanges();

    if (this.timer.minutes === 0 && this.timer.seconds === 0) {
      clearInterval(this.interval.timer);
      if (this.showSkinReport) {
        // Instead of sheet, show the strip
        this.showRegimenToast = true;
        this.changeDetectionRef.detectChanges();
      } else {
        this.showRegimenPage();
      }
    }
  }

  showRegimenPage(fromToast: boolean = false): void {
    if (fromToast) this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'report-toast-view' }));
    this.showRegimen.emit();
  }

  // Start the timer and update it at regular intervals
  startIntervalTimer(updateInterval: number): void {
    this.interval.timer = setInterval(this.updateTimerAndCheckIfFinished.bind(this), updateInterval);
  }

  // Main function for starting the regimen timer
  startRegimenTimer(milliseconds: number = 0): void {
    if (milliseconds < 0) return;

    const timeToUse = Math.min(milliseconds, this.defaultTime);
    const { minutes, seconds }: { minutes: number, seconds: number } = this.calculateRemainingTime(timeToUse);
    this.timer.minutes = minutes;
    this.timer.seconds = seconds;

    // Recalculate elapsedTime for animation synchronization based on actual remaining time
    this.elapsedTime = -(this.defaultTime - timeToUse);
    if (this.elapsedTime >= this.defaultTime) this.elapsedTime = -this.defaultTime;

    const isTimeOver = this.timer.minutes === 0 && this.timer.seconds === 0;
    if (!isTimeOver) {
      this.startAnimationAfterDelay(500);
      this.startIntervalTimer(1000);
    }

    this.changeDetectionRef.detectChanges();
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  getCurrentMessageKey(): string {
    const totalSeconds = this.defaultTime / this.MILLISECONDS_PER_SECOND;
    const remainingSeconds = (this.timer.minutes * this.SECONDS_PER_MINUTE) + this.timer.seconds;
    const elapsedSeconds = totalSeconds - remainingSeconds;
    const phaseDuration = totalSeconds / this.TIMER_MESSAGE_VALUES.length;
    const currentPhase = Math.min(Math.floor(elapsedSeconds / phaseDuration), this.TIMER_MESSAGE_VALUES.length - 1);
    return this.TIMER_MESSAGE_VALUES[currentPhase];
  }

  ngOnDestroy(): void {
    clearInterval(this.interval.timer);
    clearInterval(this.interval.carousel);
  }
}
